/**
 * CropFilterPanel v2 (AK-70, обновлено AK-95 v6 2026-05-04) — левая панель карты.
 *
 * Изменения vs v1:
 *   • Площадь больше не plug — берётся из /api/v1/aggregations/crops-area
 *     (useCropsArea). Сортировка по total_ha DESC.
 *   • Отдельная row "Без посева" (crop_id='no_crop', серый swatch).
 *   • Фазная легенда штриховок ("Не засеяно (план)") под списком.
 *   • Под-секция "Подписи на карте": toggle'ы (номер поля, название культуры,
 *     иконка культуры, площадь поля).
 *
 * v6 (founder 2026-05-04): unified «По цвету культуры» — ОДИН toggle вместо
 * двух (cropColorFillOn + phaseFillOn). Цвет полигона = культура, штриховка =
 * статус (planned → полосы, growing → solid, harvested → точки).
 *
 * Click на crop row → focus mode (как было): setFocusedCropId(id|null).
 * Hover → bg-accent (стандартная подсветка).
 *
 * Scope-aware: useCropsArea(activeScope). Refetch автоматический при смене
 * scope (новая queryKey). Дополнительный invalidate на событии scopeChanged.
 */
import { useQueryClient } from '@tanstack/react-query';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { useCropsArea } from '@/lib/map/useCropsArea';
import { useMapStore } from '@/lib/map/useMapStore';
import { useScopeChanged } from '@/lib/scopeEvent';
import { DraggablePanel } from './DraggablePanel';
import { MapSettingsPopover } from './MapSettingsPopover';
import { cn } from '@/lib/utils';

interface CropFilterPanelProps {
  mobile?: boolean;
}

/** Форматирует число гектаров: "11 848 га" / "853 га" / "0,4 га" (для small). */
function formatHa(ha: number): string {
  if (ha >= 100) {
    // Без дроби, тысячные разделители — non-breaking space
    return `${Math.round(ha).toLocaleString('ru-RU').replace(/ /g, ' ')} га`;
  }
  if (ha >= 10) {
    return `${ha.toFixed(0)} га`;
  }
  return `${ha.toFixed(1).replace('.', ',')} га`;
}

export function CropFilterPanel({ mobile = false }: CropFilterPanelProps) {
  const activeScope = useMapStore((s) => s.activeScope);
  const cropsArea = useCropsArea(activeScope);
  const queryClient = useQueryClient();

  // Авто-refetch на смене scope. Сама смена activeScope меняет queryKey →
  // TanStack делает refetch. Доп. invalidate ловит фоновые изменения посевов
  // в других вкладках (например, кто-то закрыл sowing в Field detail).
  useScopeChanged(() => {
    queryClient.invalidateQueries({ queryKey: ['crops-area'] });
  });

  const focusedCropId = useMapStore((s) => s.focusedCropId);
  const setFocusedCropId = useMapStore((s) => s.setFocusedCropId);
  // 4 toggle'а подписей (fieldNumberOn / cropNameOn / cropIconsOn / fieldAreaOn)
  // перенесены в `MapSettingsPopover` (AK-186, Phase 2 PR-3b, Path B). State
  // источник тот же — `useMapStore`. Здесь оставлен только `cropColorFillOn`
  // для секции «Заливка полей» (отдельный UX-блок, не часть labels).
  const cropColorFillOn = useMapStore((s) => s.cropColorFillOn);
  const setCropColorFillOn = useMapStore((s) => s.setCropColorFillOn);

  const handleCropClick = (cropId: string) => {
    // 'no_crop' пока тоже toggle'абельный (можно подсветить поля без посева).
    setFocusedCropId(focusedCropId === cropId ? null : cropId);
  };

  const items = cropsArea.data?.items ?? [];

  return (
    <DraggablePanel
      panelId="crops"
      title="Культуры"
      disabled={mobile}
      className={cn(!mobile && 'w-[280px]')}
    >
      <ScrollArea className={cn('px-2', mobile ? 'max-h-[40vh]' : 'max-h-[50vh]')}>
        <div className="py-2 space-y-1">
          {cropsArea.isLoading && (
            <>
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </>
          )}
          {cropsArea.isError && (
            <div className="px-2 py-3 text-xs text-destructive">
              Ошибка загрузки культур
            </div>
          )}
          {!cropsArea.isLoading && !cropsArea.isError && items.length === 0 && (
            <div className="px-2 py-3 text-xs text-muted-foreground">
              {activeScope.type === 'company'
                ? 'Поля ещё не созданы'
                : 'В выбранной области нет полей'}
            </div>
          )}
          {items.map((crop) => {
            const isFocused = focusedCropId === crop.crop_id;
            const isDimmed = focusedCropId !== null && !isFocused;
            const isNoCrop = crop.crop_id === 'no_crop';
            return (
              <button
                key={crop.crop_id}
                type="button"
                onClick={() => handleCropClick(crop.crop_id)}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-2 rounded-md text-left',
                  'hover:bg-accent transition-colors',
                  isFocused && 'bg-primary/10 ring-1 ring-primary/40',
                  isDimmed && 'opacity-50',
                )}
                title={`${crop.name} · ${crop.fields_count} ${
                  crop.fields_count === 1 ? 'поле' : 'полей'
                }`}
              >
                <span
                  className={cn(
                    'h-3 w-3 rounded-sm shrink-0 border',
                    isNoCrop ? 'border-border' : 'border-border/60',
                  )}
                  style={{ backgroundColor: crop.color }}
                  aria-hidden
                />
                <span
                  className={cn(
                    'text-sm flex-1 truncate',
                    isNoCrop ? 'text-muted-foreground' : 'text-foreground',
                  )}
                >
                  {crop.name}
                </span>
                <span
                  className={cn(
                    'text-xs font-mono tabular-nums shrink-0',
                    isNoCrop ? 'text-muted-foreground' : 'text-foreground/80',
                  )}
                >
                  {formatHa(crop.total_ha)}
                </span>
              </button>
            );
          })}
        </div>
      </ScrollArea>

      {/* Phase legend (штриховки на карте) */}
      <Separator />
      <div className="px-3 py-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span
            className="inline-block h-3 w-6 rounded-sm border border-border"
            aria-hidden
            style={{
              // diagonal hatch pattern: 4-px stripes, neutral muted
              backgroundImage:
                'repeating-linear-gradient(45deg, hsl(var(--muted-foreground) / 0.35) 0 2px, transparent 2px 5px)',
              backgroundColor: 'hsl(var(--muted) / 0.4)',
            }}
          />
          <span>Не засеяно (план)</span>
        </div>
      </div>

      <Separator />

      <div className="px-3 py-3 space-y-2">
        {/*
          AK-186 Phase 2 PR-3b: 4 toggle'а «Подписи на карте» (номер поля,
          название культуры, иконка культуры, площадь поля) свёрнуты в
          MapSettingsPopover за gear-кнопкой. Поведение и state — те же.
        */}
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Подписи на карте
          </div>
          <MapSettingsPopover />
        </div>

        <div className="pt-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Заливка полей
        </div>
        <label className="flex items-center justify-between gap-2 text-sm cursor-pointer">
          <span className="text-foreground">По цвету культуры</span>
          <Switch checked={cropColorFillOn} onCheckedChange={setCropColorFillOn} />
        </label>
      </div>
    </DraggablePanel>
  );
}
